import { useEffect, useMemo, useState } from 'react';
import Modal from './Modal';
import PosSignupReviewModal from './PosSignupReviewModal';
import { useConfig } from '../context/ConfigContext';
import { spinnerRing } from '../utils/bookingUtils';
import { formatMoney } from '../utils/currencyUtil';
import { subscribeSignupRequests } from '../utils/posSignupUtil';
import {
  POS_SIGNUP_STATUS, POS_SIGNUP_STATUS_LABELS, POS_SIGNUP_TYPES, POS_SIGNUP_TYPE_LABELS,
} from '../constants/posConst';
import '../css/PosAdmin.css';
import '../css/PosSubscription.css';

/** The filter tabs, in order. `null` status shows everything. */
const FILTERS = [
  { key: POS_SIGNUP_STATUS.PENDING, label: 'New' },
  { key: POS_SIGNUP_STATUS.DONE, label: 'Done' },
  { key: POS_SIGNUP_STATUS.DISMISSED, label: 'Dismissed' },
  { key: 'all', label: 'All' },
];

/** Status pill tone, reusing the application pills' colours. */
const PILL_TONE = {
  [POS_SIGNUP_STATUS.PENDING]: 'ps-app-pending',
  [POS_SIGNUP_STATUS.DONE]: 'ps-app-approved',
  [POS_SIGNUP_STATUS.DISMISSED]: 'ps-app-rejected',
};

/**
 * /admin -> Sign-up Requests: what visitors asked for on the landing page —
 * paid subscriptions waiting for their receipt to be checked, and demo calls
 * for the custom plan.
 *
 * A request is worked by hand: check the receipt, register the client on
 * Client Registration, email them, then mark the request Done. New requests
 * list oldest first — the one that has waited longest is the one to do next.
 *
 * @param {object} props
 * @param {string} props.signedInEmail - recorded as the reviewer
 * @returns {JSX.Element}
 */
function PosSignupRequests({ signedInEmail }) {
  const { config } = useConfig();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [filter, setFilter] = useState(POS_SIGNUP_STATUS.PENDING);
  const [reviewingId, setReviewingId] = useState(null);
  const [result, setResult] = useState(null);

  useEffect(() => subscribeSignupRequests(
    (list) => { setRequests(list); setLoading(false); setLoadError(null); },
    (error) => { setLoadError(error); setLoading(false); },
  ), []);

  const counts = useMemo(() => requests.reduce((acc, request) => {
    acc[request.status] = (acc[request.status] || 0) + 1;
    return acc;
  }, {}), [requests]);

  const shown = useMemo(() => {
    const list = filter === 'all' ? requests : requests.filter((request) => request.status === filter);
    // New ones oldest first (a queue); everything else newest first (a history).
    return filter === POS_SIGNUP_STATUS.PENDING
      ? [...list].sort((a, b) => (a.submittedAt || 0) - (b.submittedAt || 0))
      : list;
  }, [requests, filter]);

  // Looked up live, so the dialog shows a status change the moment it lands.
  const reviewing = requests.find((request) => request.id === reviewingId) || null;

  if (loading) {
    return <div className="pa-loading">{spinnerRing('Loading sign-up requests')}</div>;
  }

  return (
    <div className="pa-screen ps-screen-wide">
      <header className="pa-screen-head">
        <div>
          <h1 className="pa-screen-title">Sign-up Requests</h1>
          <p className="pa-screen-sub">
            Subscriptions and demo calls from the landing page. Check the receipt, register the client
            on Client Registration, email them their login, then mark the request Done.
          </p>
        </div>
      </header>

      <div className="pa-tabs" role="tablist" aria-label="Filter requests">
        {FILTERS.map(({ key, label }) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={filter === key}
            className={`pa-tab${filter === key ? ' active' : ''}`}
            onClick={() => setFilter(key)}
          >
            {label}
            {key !== 'all' && counts[key] ? ` (${counts[key]})` : ''}
          </button>
        ))}
      </div>

      {loadError ? (
        <div className="pa-empty">
          <h2 className="pa-empty-title">The requests could not be read</h2>
          <p className="pa-empty-body">{loadError.message}</p>
          <p className="pa-empty-body">
            Usually the database rules have not been deployed yet
            (<code className="pa-code">firebase deploy --only database</code>).
          </p>
        </div>
      ) : shown.length === 0 ? (
        <div className="pa-empty">
          <h2 className="pa-empty-title">Nothing here</h2>
          <p className="pa-empty-body">
            {filter === POS_SIGNUP_STATUS.PENDING
              ? 'No new requests. Subscriptions and demo calls from the landing page will show up here.'
              : 'No requests in this list yet.'}
          </p>
        </div>
      ) : (
        <div className="pa-cards">
          {shown.map((request) => (
            <article className="pa-card" key={request.id}>
              <div className="pa-card-head">
                <div className="pa-card-ident">
                  <h2 className="pa-card-name">{request.business}</h2>
                  <span className={`ps-pill ${PILL_TONE[request.status] || ''}`}>
                    {POS_SIGNUP_STATUS_LABELS[request.status] || request.status}
                  </span>
                </div>
              </div>
              <dl className="pa-card-meta">
                <div><dt>Type</dt><dd>{POS_SIGNUP_TYPE_LABELS[request.type] || request.type}</dd></div>
                <div><dt>Name</dt><dd>{request.name}</dd></div>
                <div><dt>Plan</dt><dd>{request.planName || '—'}</dd></div>
                {request.type === POS_SIGNUP_TYPES.SUBSCRIBE && (
                  <div><dt>Total</dt><dd>{formatMoney(request.total, config)}</dd></div>
                )}
                {request.type === POS_SIGNUP_TYPES.DEMO && (
                  <div><dt>Call</dt><dd>{request.callDate} · {request.slotLabel}</dd></div>
                )}
                <div><dt>Sent</dt><dd>{new Date(request.submittedAt).toLocaleString()}</dd></div>
              </dl>
              <div className="pa-card-actions">
                <button type="button" className="pa-btn pa-btn-primary" onClick={() => setReviewingId(request.id)}>
                  {request.status === POS_SIGNUP_STATUS.PENDING ? 'Review' : 'View'}
                </button>
              </div>
            </article>
          ))}
        </div>
      )}

      <PosSignupReviewModal
        request={reviewing}
        config={config}
        signedInEmail={signedInEmail}
        onClose={() => setReviewingId(null)}
        onDone={(outcome) => { setReviewingId(null); setResult(outcome); }}
      />

      <Modal
        isOpen={Boolean(result)}
        onClose={() => setResult(null)}
        title={result?.title || ''}
        message={result?.message || ''}
        type={result?.type || 'info'}
      />
    </div>
  );
}

export default PosSignupRequests;
